import React from 'react';
import { theme } from '../../config/theme';

const Button = ({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  loading = false,
  icon,
  className = '',
  style = {},
  ...props
}) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    fontWeight: theme.typography.fontWeight.medium,
    borderRadius: theme.borderRadius.md,
    transition: theme.transitions.base,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    outline: 'none',
    fontFamily: theme.typography.fontFamily,
    opacity: disabled || loading ? 0.6 : 1,
    border: 'none',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
  };

  const variants = {
    primary: {
      backgroundColor: theme.colors.primary.main,
      color: theme.colors.white,
      border: 'none',
      hoverStyles: {
        backgroundColor: theme.colors.primary.dark,
        boxShadow: theme.shadows.sm,
      },
    },
    secondary: {
      backgroundColor: theme.colors.white,
      color: theme.colors.text.primary,
      border: `1px solid ${theme.colors.gray[300]}`,
      hoverStyles: {
        backgroundColor: theme.colors.gray[50],
        borderColor: theme.colors.gray[400],
      },
    },
    outline: {
      backgroundColor: 'transparent',
      color: theme.colors.primary.main,
      border: `1px solid ${theme.colors.primary.main}`,
      hoverStyles: {
        backgroundColor: `${theme.colors.primary.main}10`,
      },
    },
    ghost: {
      backgroundColor: 'transparent',
      color: theme.colors.primary.main,
      border: 'none',
      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
      hoverStyles: {
        backgroundColor: theme.colors.gray[100],
      },
    },
    danger: {
      backgroundColor: theme.colors.error,
      color: theme.colors.white,
      border: 'none',
      hoverStyles: {
        backgroundColor: '#dc2626',
        boxShadow: theme.shadows.sm,
      },
    },
    'danger-outline': {
      backgroundColor: 'transparent',
      color: theme.colors.error,
      border: `1px solid ${theme.colors.error}`,
      hoverStyles: {
        backgroundColor: `${theme.colors.error}10`,
      },
    },
    success: {
      backgroundColor: theme.colors.success,
      color: theme.colors.white,
      border: 'none',
      hoverStyles: {
        backgroundColor: '#059669',
        boxShadow: theme.shadows.sm,
      },
    },
    link: {
      backgroundColor: 'transparent',
      color: theme.colors.primary.main,
      border: 'none',
      padding: '0',
      textDecoration: 'underline',
      hoverStyles: {
        color: theme.colors.primary.dark,
      },
    },
  };

  const sizes = {
    xs: {
      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
      fontSize: theme.typography.fontSize.xs,
      minHeight: '28px',
    },
    sm: {
      padding: `${theme.spacing.xs} ${theme.spacing.md}`,
      fontSize: theme.typography.fontSize.sm,
      minHeight: '32px',
    },
    md: {
      padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
      fontSize: theme.typography.fontSize.sm,
      minHeight: '40px',
    },
    lg: {
      padding: `${theme.spacing.md} ${theme.spacing.xl}`,
      fontSize: theme.typography.fontSize.base,
      minHeight: '48px',
    },
  };

  const variantStyles = variants[variant] || variants.primary;
  const sizeStyles = sizes[size] || sizes.md;

  // Override size padding for ghost variant if not explicitly overridden
  const finalSizeStyles = variant === 'ghost' && !style.padding
    ? { ...sizeStyles }
    : sizeStyles;

  const computedStyles = {
    ...baseStyles,
    ...variantStyles,
    ...finalSizeStyles,
    width: fullWidth ? '100%' : 'auto',
  };

  // Apply hover styles
  const finalStyles = isHovered && !disabled && !loading
    ? { ...computedStyles, ...variantStyles.hoverStyles, ...style }
    : { ...computedStyles, ...style };

  const handleMouseEnter = () => {
    if (!disabled && !loading) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      style={finalStyles}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {loading && (
        <svg
          style={{ animation: 'spin 1s linear infinite' }}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
            strokeOpacity="0.3"
          />
          <path
            d="M12 2a10 10 0 0 1 10 10"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinecap="round"
          />
        </svg>
      )}
      {icon && !loading && (
        <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>
      )}
      {children}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </button>
  );
};

export default Button;